// The plan as waves (D87): a subtask with no `after` is wave 1, any other one wave after the
// latest of those it follows. A dependency the plan does not list, or a cycle (the API refuses
// both, so only a stale or hand-made plan has one), counts as wave 1 rather than hanging.
import type { Messages } from "../../i18n/messages/en";
import type { Tone } from "../../ui/tones";
import { TASK_TONE, shownStatus } from "../display";
import { TASK_STATUSES, type TaskStatus } from "../model";
import type { Child, Plan, PlanItem, WaitingOn } from "./api";

export interface Wave {
  number: number;
  items: PlanItem[];
}

export function waveNumbers(items: PlanItem[]): Map<string, number> {
  const byTask = new Map(items.map((i) => [i.task, i]));
  const waves = new Map<string, number>();
  const visiting = new Set<string>();
  const wave = (task: string): number => {
    const known = waves.get(task);
    if (known !== undefined) return known;
    const item = byTask.get(task);
    if (!item || visiting.has(task)) return 0;
    visiting.add(task);
    const n = 1 + Math.max(0, ...item.after.map(wave));
    visiting.delete(task);
    waves.set(task, n);
    return n;
  };
  for (const i of items) wave(i.task);
  return waves;
}

export function planWaves(plan: Plan): Wave[] {
  const numbers = waveNumbers(plan.subtasks);
  const out = new Map<number, PlanItem[]>();
  for (const item of plan.subtasks) {
    const n = numbers.get(item.task) ?? 1;
    out.set(n, [...(out.get(n) ?? []), item]);
  }
  return [...out.entries()].sort(([a], [b]) => a - b).map(([number, items]) => ({ number, items }));
}

/** A child waits on its owner: an answer, an approval or a VPN sign-in of its own. */
export const needsOwner = (w: WaitingOn | null): boolean => w?.kind === "questions" || w?.kind === "approval" || w?.kind === "vpn";

export interface Progress {
  done: number;
  total: number;
  needsYou: number;
  failed: number;
  running: number;
}

/** From the children themselves: what the card's header and the progress line say. */
export function progress(children: Child[]): Progress {
  let done = 0;
  let needsYou = 0;
  let failed = 0;
  let running = 0;
  for (const c of children) {
    if (c.status === "succeeded") done++;
    else if (c.status === "failed" || c.status === "cancelled") failed++;
    else if (needsOwner(c.waiting_on) || c.status === "waiting_for_human") needsYou++;
    else if (c.status === "running") running++;
  }
  return { done, total: children.length, needsYou, failed, running };
}

/** The child a plan item names: matched by source ref, then by id. */
export function childFor(children: Child[], task: string): Child | undefined {
  return children.find((c) => c.ref === task) ?? children.find((c) => c.id === task);
}

const asStatus = (s: string): TaskStatus => ((TASK_STATUSES as readonly string[]).includes(s) ? (s as TaskStatus) : "queued");

/** A child in the shape the status pill reads. */
export const pillTask = (c: Child) => ({ status: asStatus(c.status), waiting_on: c.waiting_on });

/** A child's colour, as its pill shows it. */
export const childTone = (c: Child): Tone => TASK_TONE[shownStatus(pillTask(c))];

/** "Orchestrating · 2 running · 1 needs you" (D87), the counts that are not zero. */
export function orchestratingLabel(w: Extract<WaitingOn, { kind: "subtasks" }>, t: Messages["tasks"]["subtasks"]): string {
  const parts = [t.orchestrating];
  if (w.running > 0) parts.push(t.runningCount(w.running));
  if (w.waiting > 0) parts.push(t.needsYouCount(w.waiting));
  if (w.failed > 0) parts.push(t.failedCount(w.failed));
  if (parts.length === 1) parts.push(t.progress(w.done, w.total));
  return parts.join(" · ");
}
